import {
  PATIENT_STATUS_LABELS,
  type PatientStatus,
  type PatientTrackerFieldPolicy,
  type TrackerEntryDto
} from './patientTrackingTypes';
import {
  RecordDetailGrid,
  RecordDetailGridFull,
  RecordDetailHero,
  RecordDetailItem,
  RecordDetailPanel,
  RecordDetailView,
  type RecordDetailBadge
} from './RecordDetailView';
import './recordDetailView.css';

function statusBadge(status: PatientStatus): RecordDetailBadge {
  if (status === 'Resolved') return { text: PATIENT_STATUS_LABELS[status], tone: 'success' };
  if (status === 'Lost') return { text: PATIENT_STATUS_LABELS[status], tone: 'danger' };
  if (status === 'OnHold') return { text: PATIENT_STATUS_LABELS[status], tone: 'warning' };
  return { text: PATIENT_STATUS_LABELS[status], tone: 'info' };
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

export function TrackerEntryDetailView({
  row,
  fieldPolicy
}: {
  row: TrackerEntryDto;
  fieldPolicy?: PatientTrackerFieldPolicy;
}) {
  const badges: RecordDetailBadge[] = [statusBadge(row.status)];
  if (row.enrollmentForm) badges.push({ text: 'Enrollment on file', tone: 'neutral' });

  const patientLine = row.patientDob
    ? `${row.patientName} · DOB ${row.patientDob}`
    : row.patientName;

  return (
    <RecordDetailView>
      <RecordDetailHero
        title={row.currentIssue.trim() || 'Tracker issue'}
        subtitle={`${patientLine}${row.pharmacyName ? ` · ${row.pharmacyName}` : ''}`}
        badges={badges}
      />

      {!fieldPolicy?.hidePharmacy && !fieldPolicy?.hideCoveredEntity ? (
        <RecordDetailPanel title="Assignment">
          <RecordDetailGrid>
            {!fieldPolicy?.hideCoveredEntity ? (
              <RecordDetailItem label="CE to alert" value={row.coveredEntityName} />
            ) : null}
            {!fieldPolicy?.hidePharmacy ? (
              <RecordDetailItem label="Pharmacy" value={row.pharmacyName} />
            ) : null}
            <RecordDetailItem label="Patient" value={row.patientName} />
            <RecordDetailItem label="DOB" value={row.patientDob} />
          </RecordDetailGrid>
        </RecordDetailPanel>
      ) : (
        <RecordDetailPanel title="Patient">
          <RecordDetailGrid>
            <RecordDetailItem label="Patient" value={row.patientName} />
            <RecordDetailItem label="DOB" value={row.patientDob} />
          </RecordDetailGrid>
        </RecordDetailPanel>
      )}

      <RecordDetailPanel title="Issue details">
        <RecordDetailGrid columns={1}>
          <RecordDetailGridFull>
            <RecordDetailItem label="Current issue" value={row.currentIssue} multiline />
          </RecordDetailGridFull>
          <RecordDetailGridFull>
            <RecordDetailItem label="Communication history" value={row.changeLog} multiline />
          </RecordDetailGridFull>
          {!fieldPolicy?.hideInternalNote ? (
            <RecordDetailGridFull>
              <RecordDetailItem label="Internal note (pharmacy only)" value={row.internalNote} multiline />
            </RecordDetailGridFull>
          ) : null}
        </RecordDetailGrid>
      </RecordDetailPanel>

      <RecordDetailPanel title="Alert activity">
        <RecordDetailGrid>
          <RecordDetailItem label="Emails sent" value={String(row.emailsSent)} />
          <RecordDetailItem label="Last sent" value={formatDateTime(row.emailLastSent) || null} />
          <RecordDetailItem label="Next alert" value={formatDateTime(row.nextAlertDate) || null} />
          <RecordDetailItem label="Modified by" value={row.modifiedBy} />
        </RecordDetailGrid>
      </RecordDetailPanel>
    </RecordDetailView>
  );
}
